import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { QrCode, Shield, Users, Printer, Heart, Zap, LogOut, Edit, Eye, LayoutDashboard, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { User } from "@supabase/supabase-js";
import { useSubscription } from "@/hooks/useSubscription";
import { trackAuthAction, trackButtonClick, trackNavigationClick } from "@/utils/analytics";
import { MetaTags, getOrganizationSchema, getWebApplicationSchema, getFAQSchema } from "@/components/MetaTags";
import { SocialShare } from "@/components/SocialShare";

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  photo_url: string;
  created_at: string;
}

const Index = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const { isPremium, isPro } = useSubscription();

  useEffect(() => {
    // Check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProfiles(session.user.id);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProfiles(session.user.id);
      } else {
        setProfiles([]);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadUserProfiles = async (userId: string) => {
    setLoadingProfiles(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, photo_url, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error loading profiles:", error);
        toast.error("Fehler beim Laden der Profile");
      } else {
        setProfiles(data || []);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoadingProfiles(false);
    }
  };

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error("Fehler beim Abmelden");
    } else {
      toast.success("Erfolgreich abgemeldet");
      trackAuthAction('logout');
      navigate("/");
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-portal-session");

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      console.error("Error opening portal:", error);
      toast.error("Fehler beim Öffnen des Kundenportals: " + error.message);
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <>
      <MetaTags
        title="MediQR - Digitale Notfallhilfe | Lebensrettende QR-Codes"
        description="Erstellen Sie personalisierte QR-Codes mit lebensrettenden Notfallinformationen. Medikamente, Allergien, Notfallkontakte - sofort verfügbar für Rettungskräfte."
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            getOrganizationSchema(),
            getWebApplicationSchema(),
            getFAQSchema()
          ]
        }}
      />
      <div className="min-h-screen">
      {/* User Profiles Section - Only visible when logged in */}
      {user && profiles.length > 0 && (
        <section className="py-24 px-4 bg-background pt-32">
          <div className="container mx-auto max-w-6xl">
            <div className="mb-8">
              <h2 className="text-3xl font-bold mb-2">
                Ihre <span className="gradient-text">Profile</span>
              </h2>
              <p className="text-muted-foreground">
                Verwalten Sie Ihre erstellten Notfallprofile
              </p>
            </div>

            {loadingProfiles ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Lädt Profile...</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {profiles.map((profile) => (
                  <Card key={profile.id} className="p-6 glass hover:shadow-glass transition-all duration-300">
                    <div className="flex items-start gap-4 mb-4">
                      <img
                        src={profile.photo_url}
                        alt={`${profile.first_name} ${profile.last_name}`}
                        className="w-16 h-16 rounded-full object-cover border-2 border-primary/20"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold truncate">
                          {profile.first_name} {profile.last_name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Erstellt: {new Date(profile.created_at).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Link 
                        to={`/create?edit=${profile.id}`} 
                        className="flex-1"
                        onClick={() => trackButtonClick('edit_profile', 'profile_card')}
                      >
                        <Button variant="outline" className="w-full">
                          <Edit className="mr-2 h-4 w-4" />
                          Bearbeiten
                        </Button>
                      </Link>
                      <Link 
                        to={`/qr/${profile.id}`} 
                        className="flex-1"
                        onClick={() => trackButtonClick('view_qr', 'profile_card')}
                      >
                        <Button className="w-full bg-gradient-primary">
                          <Eye className="mr-2 h-4 w-4" />
                          QR-Code
                        </Button>
                      </Link>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Hero Section */}
      <section className={`relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-subtle ${user && profiles.length > 0 ? 'pt-20' : 'pt-20'}`}>
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/20 rounded-full blur-3xl animate-glow-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-glow-pulse" style={{ animationDelay: "1s" }} />
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center space-y-8 animate-slide-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-primary/20 mb-4">
              <Shield className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Notfall-Informationen immer griffbereit</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight">
              Im Notfall zählt <span className="gradient-text">jede Sekunde</span>
            </h1>
            
            <p className="text-2xl md:text-3xl text-foreground font-semibold mb-4">
              Ihre medizinischen Infos, sofort verfügbar per QR-Code
            </p>
            
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              MediQR speichert wichtige Notfalldaten sicher und macht sie für Rettungsdienste mit einem Scan sichtbar.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <Link to="/create" onClick={() => trackButtonClick('create_qr_code', 'hero_cta')}>
                <Button size="lg" className="bg-gradient-primary hover:shadow-glow transition-all duration-300 text-lg px-8 h-14">
                  <QrCode className="mr-2 h-5 w-5" />
                  Jetzt Notfallprofil anlegen
                </Button>
              </Link>
              <Link to="/how-it-works" onClick={() => trackNavigationClick('how_it_works', 'hero')}>
                <Button size="lg" variant="outline" className="border-2 border-primary/30 hover:bg-primary/5 text-lg px-8 h-14">
                  So funktioniert's
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 pt-12 max-w-2xl mx-auto">
              <div className="space-y-2">
                <div className="text-3xl font-bold gradient-text">24/7</div>
                <div className="text-sm text-muted-foreground">Verfügbar</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold gradient-text">Sicher</div>
                <div className="text-sm text-muted-foreground">Verschlüsselt</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold gradient-text">Einfach</div>
                <div className="text-sm text-muted-foreground">Handhabung</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-primary/30 flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-primary rounded-full" />
          </div>
        </div>
      </section>

      {/* Target Groups Section */}
      <section className="py-24 px-4 bg-gradient-subtle">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-4xl font-bold mb-4">
              Für wen ist <span className="gradient-text">MediQR</span>?
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              MediQR ist für jeden, dem seine Gesundheit und die seiner Liebsten wichtig ist. 
              Besonders hilfreich ist es für:
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Users,
                title: "Senior:innen",
                description: "Bei mehreren Medikamenten und Vorerkrankungen ist schneller Zugriff auf Gesundheitsdaten entscheidend. MediQR gibt alle wichtigen Informationen sofort weiter."
              },
              {
                icon: Heart,
                title: "Chronisch Erkrankte",
                description: "Bei Diabetes, Herzerkrankungen oder Allergien müssen Rettungskräfte schnell informiert sein. MediQR stellt sicher, dass nichts übersehen wird."
              },
              {
                icon: Users,
                title: "Familien",
                description: "Schützen Sie Ihre ganze Familie – von den Kleinsten bis zu den Großeltern. Mit MediQR haben Sie alle wichtigen Gesundheitsinformationen Ihrer Liebsten an einem Ort."
              },
              {
                icon: Zap,
                title: "Aktive Menschen",
                description: "Beim Sport, auf Reisen oder im Alltag – Unfälle können überall passieren. MediQR sorgt dafür, dass Ihre medizinischen Daten immer verfügbar sind."
              }
            ].map((group, index) => (
              <div
                key={index}
                className="group p-6 rounded-2xl glass hover:shadow-glass transition-all duration-300 animate-scale-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center mb-4 group-hover:shadow-glow transition-all duration-300">
                  <group.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{group.title}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{group.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-4xl font-bold mb-4">
              Warum <span className="gradient-text">MediQR</span>?
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Im Notfall zählt jede Sekunde. Mit MediQR haben Rettungskräfte sofort Zugriff auf lebenswichtige Informationen.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: QrCode,
                title: "QR-Code mit Foto",
                description: "Generieren Sie individuelle QR-Codes mit eingebettetem Foto für klare Identifizierung."
              },
              {
                icon: Shield,
                title: "Notfallinformationen",
                description: "Medikamente, Allergien, Vorerkrankungen und Notfallkontakte immer verfügbar."
              },
              {
                icon: Users,
                title: "Für die ganze Familie",
                description: "Erstellen Sie Profile für alle Familienmitglieder an einem Ort."
              },
              {
                icon: Printer,
                title: "Druckfertig",
                description: "Optimiert für Kühlschrank, Geldbeutel oder überall wo es benötigt wird."
              },
              {
                icon: Heart,
                title: "Lebensrettend",
                description: "Kritische Informationen in Sekunden verfügbar für Ersthelfer."
              },
              {
                icon: Zap,
                title: "Blitzschnell",
                description: "Scannen und sofort Zugriff auf alle wichtigen Daten erhalten."
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="group p-8 rounded-2xl glass hover:shadow-glass transition-all duration-300 animate-scale-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-primary flex items-center justify-center mb-4 group-hover:shadow-glow transition-all duration-300">
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-gradient-subtle relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-accent/20 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="text-center space-y-8 p-12 rounded-3xl glass">
            <h2 className="text-4xl md:text-5xl font-bold">
              Bereit für mehr <span className="gradient-text">Sicherheit</span>?
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Erstellen Sie jetzt Ihren ersten MediQR-Code und schützen Sie sich und Ihre Liebsten im Notfall.
            </p>
            <Link to="/create" onClick={() => trackButtonClick('start_free', 'bottom_cta')}>
              <Button size="lg" className="bg-gradient-primary hover:shadow-accent-glow transition-all duration-300 text-lg px-10 h-16">
                <QrCode className="mr-2 h-6 w-6" />
                Kostenlos starten
              </Button>
            </Link>
            
            <div className="mt-8 pt-8 border-t border-border/50">
              <SocialShare
                title="MediQR - Lebensrettende Notfall QR-Codes"
                description="Im Notfall zählt jede Sekunde! Erstelle jetzt deinen persönlichen MediQR-Code mit wichtigen Gesundheitsinformationen für Rettungskräfte."
                hashtags={["MediQR", "Notfallhilfe", "Gesundheit", "Lebensretter"]}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-background border-t border-border">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-primary flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-lg gradient-text">MediQR</span>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div className="flex gap-4 text-sm">
                <Link to="/how-it-works" className="text-primary hover:underline">
                  So funktioniert's
                </Link>
                <Link to="/pricing" className="text-primary hover:underline">
                  Preise
                </Link>
                <Link to="/impressum" className="text-primary hover:underline">
                  Impressum
                </Link>
                <Link to="/datenschutz" className="text-primary hover:underline">
                  Datenschutz
                </Link>
                <Link to="/agb" className="text-primary hover:underline">
                  AGB
                </Link>
                <Link to="/widerrufsrecht" className="text-primary hover:underline">
                  Widerrufsrecht
                </Link>
              </div>
              <div className="text-sm text-muted-foreground">
                © 2024 MediQR by <a href="https://diekerit.com?referer=mediqr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">DiekerIT</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
      </div>
    </>
  );
};

export default Index;
